import React, { useState, useRef, useEffect, useMemo } from "react";
import { videosApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import { compressImage } from "../../utils/imageCompressor";
import { getImageUrl, handleImageError } from "../../utils/imageUrl";
import "./Videos.scss";

const CATEGORIES = ["Lectures", "Events", "Campus Life", "Student Testimonials", "Workshops", "Other"];

const BLANK_VIDEO_FORM = { _id: null, Video_Title: "", Category: "Events", Playlist_ID: "", Video_URL: "", Thumbnail_URL: "", Description: "", Date: new Date().toISOString().slice(0, 10), Mark_as_Featured: false, Visible_on_Website: true };
const BLANK_PLAYLIST_FORM = { _id: null, Playlist_Name: "", Description: "", Cover_Image_URL: "" };

const Videos = () => {
  const [playlists, setPlaylists] = useState([]);
  const [videos, setVideos]       = useState([]);
  const [loading, setLoading]     = useState(false);

  const [openPlaylistId, setOpenPlaylistId] = useState(null);
  const [search, setSearch]                 = useState("");
  const [mainView, setMainView]             = useState("grid");

  const [videoModal, setVideoModal]         = useState("");
  const [videoForm, setVideoForm]           = useState(BLANK_VIDEO_FORM);
  const [playlistModal, setPlaylistModal]   = useState("");
  const [playlistForm, setPlaylistForm]     = useState(BLANK_PLAYLIST_FORM);

  const [confirmState, setConfirmState]     = useState({ type: "", id: null });
  const [dropdown, setDropdown]             = useState({ type: "", id: null, top: 0, left: 0 });
  const [selectedFile, setSelectedFile]         = useState(null);
  const [previewUrl, setPreviewUrl]             = useState(null);
  const dropdownRef                         = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plRes, vidRes] = await Promise.all([
        apiClient(`${videosApi}/playlists`),
        apiClient(`${videosApi}/videos`)
      ]);
      const plResult = await plRes.json();
      const vidResult = await vidRes.json();
      if (plRes.ok) setPlaylists(plResult.data || []);
      if (vidRes.ok) setVideos(vidResult.data || []);
    } catch (error) {
      console.error("Error fetching video data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdown({ type: "", id: null, top: 0, left: 0 }); };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const filteredVideos = useMemo(() => {
    return videos.filter((v) => {
      const matchPlaylist = !openPlaylistId || v.Playlist_ID === openPlaylistId || v.Playlist_ID?._id === openPlaylistId;
      const matchSearch   = !search || (v.Video_Title || "").toLowerCase().includes(search.toLowerCase());
      return matchPlaylist && matchSearch;
    });
  }, [videos, openPlaylistId, search]);

  const openDropdown = (e, type, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ type, id, top: rect.bottom + 8, left: rect.right - 210 });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file);
      setSelectedFile(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    }
  };

  const removePhoto = (type) => {
    if (type === 'playlist') {
      setPlaylistForm(p => ({ ...p, Cover_Image_URL: "" }));
    } else {
      setVideoForm(p => ({ ...p, Thumbnail_URL: "" }));
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const savePlaylist = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = playlistModal === "add" ? "POST" : "PUT";
      const url = playlistModal === "add" ? `${videosApi}/playlists` : `${videosApi}/playlists/${playlistForm._id}`;
      
      let imageString = playlistForm.Cover_Image_URL || "";
      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        imageString = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(compressed);
          reader.onloadend = () => resolve(reader.result);
        });
      }

      const payload = {
        Playlist_Name: playlistForm.Playlist_Name || "",
        Description: playlistForm.Description || "",
        Cover_Image_URL: imageString,
      };

      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchData();
        setPlaylistModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save playlist: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving playlist:", error);
      alert(`Error saving playlist: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const saveVideo = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = videoModal === "add" ? "POST" : "PUT";
      const url = videoModal === "add" ? `${videosApi}/videos` : `${videosApi}/videos/${videoForm._id}`;
      
      let imageString = videoForm.Thumbnail_URL || "";
      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        imageString = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(compressed);
          reader.onloadend = () => resolve(reader.result);
        });
      }

      const payload = {
        Video_Title: videoForm.Video_Title || "",
        Category: videoForm.Category || "Events",
        Playlist_ID: videoForm.Playlist_ID || undefined,
        Video_URL: videoForm.Video_URL || "",
        Thumbnail_URL: imageString,
        Description: videoForm.Description || "",
        Date: videoForm.Date || new Date().toISOString().slice(0, 10),
        Mark_as_Featured: !!videoForm.Mark_as_Featured,
        Visible_on_Website: videoForm.Visible_on_Website !== undefined ? videoForm.Visible_on_Website : true,
      };

      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchData();
        setVideoModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save video: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving video:", error);
      alert(`Error saving video: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const type = confirmState.type === "playlist" ? "playlists" : "videos";
      const url = `${videosApi}/${type}/${confirmState.id}`;
      const response = await apiClient(url, { method: "DELETE" });
      if (response.ok) {
        fetchData();
        setConfirmState({ type: "", id: null });
        if (confirmState.type === "playlist" && openPlaylistId === confirmState.id) setOpenPlaylistId(null);
      }
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="vd-page">
      <div className="vd-header">
        <div>
          <h1>Video Management</h1>
          <p>{openPlaylistId ? playlists.find(p => p._id === openPlaylistId)?.Playlist_Name : "Manage all educational and event videos."}</p>
        </div>
        <div className="vd-actions">
           {!openPlaylistId && <button onClick={() => { setPlaylistForm(BLANK_PLAYLIST_FORM); setSelectedFile(null); setPreviewUrl(null); setPlaylistModal("add"); }}>New Playlist</button>}
           <button className="primary" onClick={() => { setVideoForm({ ...BLANK_VIDEO_FORM, Playlist_ID: openPlaylistId || "" }); setSelectedFile(null); setPreviewUrl(null); setVideoModal("add"); }}>Add Video</button>
           {openPlaylistId && <button onClick={() => setOpenPlaylistId(null)}>Back to Playlists</button>}
        </div>
      </div>

      <div className="vd-toolbar">
         <div className="vd-search">
            <input type="text" placeholder="Search videos..." value={search} onChange={(e) => setSearch(e.target.value)} />
         </div>
         <div className="vd-tabs">
            <button className={mainView === "grid" ? "active" : ""} onClick={() => {setMainView("grid"); setOpenPlaylistId(null);}}>All Videos</button>
            <button className={mainView === "playlist" ? "active" : ""} onClick={() => setMainView("playlist")}>Playlists</button>
         </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {mainView === "playlist" && !openPlaylistId && (
        <div className="vd-playlists-grid">
           {playlists.map(pl => (
             <div key={pl._id} className="playlist-card" onClick={() => { setOpenPlaylistId(pl._id); setMainView("grid"); }}>
                <div className="playlist-cover">
                   <img src={getImageUrl(pl.Cover_Image_URL, videosApi)} onError={(e) => handleImageError(e, pl.Playlist_Name)} alt={pl.Playlist_Name} />
                </div>
                <div className="playlist-info">
                   <h3>{pl.Playlist_Name}</h3>
                   <p>{pl.Description}</p>
                   <button className="dots" onClick={(e) => openDropdown(e, "playlist", pl._id)}>⋮</button>
                </div>
             </div>
           ))}
        </div>
      )}

      {mainView === "grid" && (
        <div className="vd-videos-grid">
           {filteredVideos.map((video) => (
             <div key={video._id} className="video-card">
                <div className="video-thumb">
                   <img src={getImageUrl(video.Thumbnail_URL, videosApi)} onError={(e) => handleImageError(e, video.Video_Title)} alt={video.Video_Title} />
                   {video.Mark_as_Featured && <span className="feat-badge">★</span>}
                </div>
                <div className="video-info">
                   <h4>{video.Video_Title}</h4>
                   <p>{video.Category} · {formatDate(video.Date)}</p>
                   <button className="dots" onClick={(e) => openDropdown(e, "video", video._id)}>⋮</button>
                </div>
             </div>
           ))}
        </div>
      )}

      {dropdown.id && (
        <div ref={dropdownRef} className="vd-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left, zIndex: 9999 }}>
           {dropdown.type === "playlist" ? (
             <>
                <button onClick={() => { setPlaylistForm(playlists.find(p => p._id === dropdown.id)); setSelectedFile(null); setPreviewUrl(null); setPlaylistModal("edit"); }}>Edit Playlist</button>
                <button className="delete" onClick={() => setConfirmState({ type: "playlist", id: dropdown.id })}>Delete Playlist</button>
             </>
           ) : (
             <>
                <button onClick={() => { 
                  const v = videos.find(x => x._id === dropdown.id);
                  setVideoForm({ ...v, Date: toDateStr(v.Date), Playlist_ID: v.Playlist_ID?._id || v.Playlist_ID }); 
                  setSelectedFile(null); setPreviewUrl(null);
                  setVideoModal("edit"); 
                }}>Edit Video</button>
                <button className="delete" onClick={() => setConfirmState({ type: "video", id: dropdown.id })}>Delete Video</button>
             </>
           )}
        </div>
      )}

      {confirmState.id && (
        <div className="modal-overlay" onClick={() => setConfirmState({ type: "", id: null })}>
           <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this {confirmState.type}?</p>
              <div className="confirm-actions">
                 <button onClick={() => setConfirmState({ type: "", id: null })}>Cancel</button>
                 <button onClick={handleDelete} className="btn-danger">Delete</button>
              </div>
           </div>
        </div>
      )}

      {playlistModal && (
        <div className="vd-overlay" onClick={() => setPlaylistModal("")}>
           <div className="vd-form-modal small" onClick={(e) => e.stopPropagation()}>
              <div className="vd-modal-head">
                 <div>
                    <h2>{playlistModal === "add" ? "Create New" : "Edit"} Playlist</h2>
                    <p>Organize educational videos into collections.</p>
                 </div>
                 <button className="vd-modal-close" onClick={() => setPlaylistModal("")}>×</button>
              </div>
              <form className="vd-form" onSubmit={savePlaylist}>
                 <div className="vd-field full"><label>Playlist Name <span className="req">*</span></label><input value={playlistForm.Playlist_Name} onChange={(e) => setPlaylistForm({...playlistForm, Playlist_Name: e.target.value})} required placeholder="e.g. Computer Science Lectures" /></div>
                 <div className="vd-field full"><label>Description</label><textarea value={playlistForm.Description} onChange={(e) => setPlaylistForm({...playlistForm, Description: e.target.value})} placeholder="Describe playlist content..." rows="3" /></div>
                 <div className="vd-field full">
                    <label>Cover Image</label>
                    <div className="vd-photo-upload">
                       {(previewUrl || playlistForm.Cover_Image_URL) ? (
                          <div className="vd-photo-preview">
                             <img src={previewUrl || getImageUrl(playlistForm.Cover_Image_URL, videosApi)} onError={(e) => handleImageError(e, "Preview")} alt="Preview" />
                             <div className="vd-photo-actions">
                                <label className="vd-change-btn">Change<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                                <button type="button" className="vd-remove-btn" onClick={() => removePhoto('playlist')}>Remove</button>
                             </div>
                          </div>
                       ) : (
                          <label className="vd-upload-placeholder">
                             <div className="vd-upload-icon">📷</div>
                             <span>Click to upload playlist cover</span>
                             <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                          </label>
                       )}
                    </div>
                 </div>
                 <div className="vd-form-footer">
                    <button type="button" className="vd-btn-light" onClick={() => setPlaylistModal("")}>Cancel</button>
                    <button type="submit" className="vd-btn-primary">Save Playlist</button>
                 </div>
              </form>
           </div>
        </div>
      )}

      {videoModal && (
        <div className="vd-overlay" onClick={() => setVideoModal("")}>
           <div className="vd-form-modal" onClick={(e) => e.stopPropagation()}>
              <div className="vd-modal-head">
                 <div>
                    <h2>{videoModal === "add" ? "Add New" : "Edit"} Video</h2>
                    <p>Upload video details and thumbnail image.</p>
                 </div>
                 <button className="vd-modal-close" onClick={() => setVideoModal("")}>×</button>
              </div>
              <form className="vd-form" onSubmit={saveVideo}>
                 <div className="vd-field full"><label>Video Title <span className="req">*</span></label><input value={videoForm.Video_Title} onChange={(e) => setVideoForm({...videoForm, Video_Title: e.target.value})} required placeholder="e.g. Annual Day Keynote Address" /></div>
                 <div className="vd-field-row">
                    <div className="vd-field">
                       <label>Category</label>
                       <select value={videoForm.Category} onChange={(e) => setVideoForm({...videoForm, Category: e.target.value})}>
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                       </select>
                    </div>
                    <div className="vd-field">
                       <label>Playlist <span className="req">*</span></label>
                       <select value={videoForm.Playlist_ID} onChange={(e) => setVideoForm({...videoForm, Playlist_ID: e.target.value})} required>
                          <option value="">Select Playlist</option>
                          {playlists.map(p => <option key={p._id} value={p._id}>{p.Playlist_Name}</option>)}
                       </select>
                    </div>
                 </div>
                 <div className="vd-field full"><label>Video URL (Youtube) <span className="req">*</span></label><input value={videoForm.Video_URL} onChange={(e) => setVideoForm({...videoForm, Video_URL: e.target.value})} required placeholder="https://www.youtube.com/watch?v=..." /></div>
                 <div className="vd-field full">
                    <label>Thumbnail Image</label>
                    <div className="vd-photo-upload">
                       {(previewUrl || videoForm.Thumbnail_URL) ? (
                          <div className="vd-photo-preview">
                             <img src={previewUrl || getImageUrl(videoForm.Thumbnail_URL, videosApi)} onError={(e) => handleImageError(e, "Preview")} alt="Preview" />
                             <div className="vd-photo-actions">
                                <label className="vd-change-btn">Change<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                                <button type="button" className="vd-remove-btn" onClick={() => removePhoto('video')}>Remove</button>
                             </div>
                          </div>
                       ) : (
                          <label className="vd-upload-placeholder">
                             <div className="vd-upload-icon">📷</div>
                             <span>Click to upload thumbnail</span>
                             <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                          </label>
                       )}
                    </div>
                 </div>
                 <div className="vd-field full"><label>Description</label><textarea value={videoForm.Description} onChange={(e) => setVideoForm({...videoForm, Description: e.target.value})} placeholder="Describe the video content..." rows="3" /></div>
                 <div className="vd-field-row">
                    <div className="vd-field"><label>Date <span className="req">*</span></label><input type="date" value={videoForm.Date} onChange={(e) => setVideoForm({...videoForm, Date: e.target.value})} required /></div>
                    <div className="vd-field">
                       <label>Visibility Options</label>
                       <div className="vd-toggle-row">
                          <label className="vd-toggle-item">
                             <input type="checkbox" checked={videoForm.Mark_as_Featured} onChange={(e) => setVideoForm({...videoForm, Mark_as_Featured: e.target.checked})} />
                             <span className="vd-chk-box"></span>
                             <div>
                                <strong>Featured Video</strong>
                                <p>Highlight on home page.</p>
                             </div>
                          </label>
                       </div>
                    </div>
                 </div>
                 <div className="vd-form-footer">
                    <button type="button" className="vd-btn-light" onClick={() => setVideoModal("")}>Cancel</button>
                    <button type="submit" className="vd-btn-primary">Save Video</button>
                 </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

const toDateStr = (d) => d ? new Date(d).toISOString().split('T')[0] : "";

export default Videos;